import { ReactNode, useMemo } from 'react';
import styled, { css } from 'styled-components';
import Typography from './Typography';

export type KingpinBadgeType =
  | 'Default'
  | 'Magenta'
  | 'Purple'
  | 'Blue'
  | 'Turquoise'
  | 'Green'
  | 'Olive'
  | 'Yellow'
  | 'Orange'
  | 'Red'
  | 'Pink';

const DefaultMixin = css`
  background-color: var(--color-bg-primary);
  border-color: var(--color-border-primary);
`;

const BlueMixin = css`
  background-color: var(--color-badge-blue-bg);
  border-color: var(--color-badge-blue-border);
`;

const TurquoiseMixin = css`
  background-color: var(--color-badge-turquoise-bg);
  border-color: var(--color-badge-turquoise-border);
`;

const GreenMixin = css`
  background-color: var(--color-badge-green-bg);
  border-color: var(--color-badge-green-border);
`;

const OliveMixin = css`
  background-color: var(--color-badge-olive-bg);
  border-color: var(--color-badge-olive-border);
`;

const OrangeMixin = css`
  background-color: var(--color-badge-orange-bg);
  border-color: var(--color-badge-orange-border);
`;

const RedMixin = css`
  background-color: var(--color-badge-red-bg);
  border-color: var(--color-badge-red-border);
`;

const YellowMixin = css`
  background-color: var(--color-badge-yellow-bg);
  border-color: var(--color-badge-yellow-border);
`;

const PurpleMixin = css`
  background-color: var(--color-badge-purple-bg);
  border-color: var(--color-badge-purple-border);
`;

const MagentaMixin = css`
  background-color: var(--color-badge-magenta-bg);
  border-color: var(--color-badge-magenta-border);
`;

const PinkMixin = css`
  background-color: var(--color-badge-pink-bg);
  border-color: var(--color-badge-pink-border);
`;

const KingpinBadgeDiv = styled.div<{
  badgeType: KingpinBadgeType;
  gap?: number;
}>`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  height: 23px;
  flex: 1;
  border-radius: 6px;
  border: 1px solid;
  padding: 4px 6px;
  gap: ${(props) => (props.gap ? props.gap : 0)}px;

  ${(props) => props.badgeType === 'Blue' && BlueMixin}
  ${(props) => props.badgeType === 'Default' && DefaultMixin}
  ${(props) => props.badgeType === 'Turquoise' && TurquoiseMixin}
  ${(props) => props.badgeType === 'Green' && GreenMixin}
  ${(props) => props.badgeType === 'Olive' && OliveMixin}
  ${(props) => props.badgeType === 'Orange' && OrangeMixin}
  ${(props) => props.badgeType === 'Red' && RedMixin}
  ${(props) => props.badgeType === 'Yellow' && YellowMixin}
  ${(props) => props.badgeType === 'Purple' && PurpleMixin}
  ${(props) => props.badgeType === 'Magenta' && MagentaMixin}
  ${(props) => props.badgeType === 'Pink' && PinkMixin}
`;

const KingpinBadge = ({
  text,
  badgeType,
  gap,
  children,
}: {
  text?: string;
  children?: ReactNode;
  gap?: number;
  badgeType: KingpinBadgeType;
}) => {
  const textColor = useMemo(() => {
    switch (badgeType) {
      case 'Blue':
        return 'var(--color-badge-blue-text)';
      case 'Turquoise':
        return 'var(--color-badge-turquoise-text)';
      case 'Green':
        return 'var(--color-badge-green-text)';
      case 'Olive':
        return 'var(--color-badge-olive-text)';
      case 'Orange':
        return 'var(--color-badge-orange-text)';
      case 'Yellow':
        return 'var(--color-badge-yellow-text)';
      case 'Red':
        return 'var(--color-badge-red-text)';
      case 'Purple':
        return 'var(--color-badge-purple-text)';
      case 'Magenta':
        return 'var(--color-badge-magenta-text)';
      case 'Pink':
        return 'var(--color-badge-pink-text)';
      case 'Default':
      default:
        return 'var(--color-text-primary)';
    }
  }, [badgeType]);

  return (
    <KingpinBadgeDiv badgeType={badgeType} gap={gap}>
      {!!text && (
        <Typography.Body type={'Badge'} color={textColor}>
          {text}
        </Typography.Body>
      )}
      {children}
    </KingpinBadgeDiv>
  );
};

export default KingpinBadge;
